import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
} from "@ant-design/icons";
import { helper } from "@src/controls/controlHelper";
import request, { upload as uploadFile } from "@src/util/request";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Skeleton,
  Switch,
} from "antd";
import dayjs from "dayjs";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import QRCode from "qrcode";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Image as KonvaImage,
  Text as KonvaText,
  Layer,
  Stage,
  Transformer,
} from "react-konva";
import useImage from "use-image";
import { v4 as uuidv4 } from "uuid";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

const PX_PER_MM = 96 / 25.4;
const pxToMm = (px: number) => +(px / PX_PER_MM).toFixed(2);
const mmToPx = (mm: number) => mm * PX_PER_MM;

const ensureFontLoaded = async (family: string, size = 16) => {
  try {
    // @ts-ignore
    if (document.fonts?.load) {
      await document.fonts.load(`${size}px ${family}`);
    }
  } catch {}
};
type BaseItem = {
  id: string;
  type: "text" | "qr";
  x: number;
  y: number;
  rotation?: number;
};

type TextItem = BaseItem & {
  type: "text";
  text: string;
  // Nếu text lấy từ biến meta thì lưu khóa tại đây, ví dụ: "${event:HOME}" hoặc "${ticket:GATE}"
  bindingKey?: string;
  fontSize: number;
  fill: string;
  width: number;
  fontFamily: string;
  align: "left" | "center" | "right" | "justify";
  fontStyle: "normal" | "bold" | "italic" | "bold italic";
  textDecoration?: "" | "underline" | "line-through";
};

type QRItem = BaseItem & {
  type: "qr";
  content: string;
  // Nếu QR lấy nội dung từ biến meta thì lưu khóa tại đây
  bindingKey?: string;
  width: number;
  height: number;
  src: string; // dataURL được sinh từ content
};

type EditorItem = TextItem | QRItem;

interface TicketEditorProps {
  pageId?: number;
  onClose?: () => void;
  onSaved?: (payload: any) => void;
  eventMeta?: Array<{
    name?: string;
    description?: string;
    value?: string;
    __gid?: string;
  }>;
  ticketMeta?: Array<{
    name?: string;
    description?: string;
    value?: string;
    __gid?: string;
  }>;
  eventValues?: any;
  // Edit mode support
  layoutId?: number;
  initialPage?: any;
  initialName?: string;
  initialDefault?: boolean;
}

// removed unused Background component

const TicketLayoutEditor: React.FC<TicketEditorProps> = ({
  pageId,
  onClose,
  onSaved,
  eventMeta,
  ticketMeta,
  eventValues,
  layoutId,
  initialPage,
  initialName,
  initialDefault,
}) => {
  const [stageSize, setStageSize] = useState({ width: 600, height: 400 });
  const [bgSrc, setBgSrc] = useState<string | undefined>(undefined);
  const [bgUrl, setBgUrl] = useState<string | undefined>(undefined);
  const [bgImage, bgStatus] = useImage(bgSrc || "");
  const [elements, setElements] = useState<EditorItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exportVisible, setExportVisible] = useState(false);
  const [exportText, setExportText] = useState<string>("");
  const [pageSize, setPageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [renderScale, setRenderScale] = useState<number>(1);
  const [layoutName, setLayoutName] = useState<string>("");
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [savingLayout, setSavingLayout] = useState<boolean>(false);
  const [initLoading, setInitLoading] = useState<boolean>(true);

  const stageRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const trRef = useRef<any>(null);
  const [pdfOrigin, setPdfOrigin] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const displayScale = useMemo(() => {
    if (!containerWidth || !stageSize.width) return 1;
    // Để dư ra một chút khoảng trống để tránh thanh cuộn ngang do sai số làm tròn
    const availableWidth = containerWidth;
    if (availableWidth <= 0) return 1;
    // Scale để fit vừa chiều ngang, cho phép phóng to để dễ nhìn (Zoom to fit)
    return availableWidth / stageSize.width;
  }, [containerWidth, stageSize.width]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initialize editor from initial props (edit mode)
  useEffect(() => {
    if (!initialPage) {
      setInitLoading(false);
      return;
    }
    const loadInitialData = async () => {
      setInitLoading(true);
      try {
        const bg = initialPage?.background;
        let currentScale = 1;
        if (bg) {
          setBgUrl(bg);
          if (/(\.pdf)(\?|$)/i.test(String(bg))) {
            try {
              let pdf: any;
              try {
                const proxied = await request<ArrayBuffer>({
                  url: "/admin/ticket/proxy-pdf",
                  options: {
                    method: "post",
                    data: { url: String(bg) },
                    responseType: "arraybuffer",
                    headers: { Accept: "application/pdf" },
                  },
                });
                const buf = proxied?.data as any;
                pdf = await pdfjsLib.getDocument({ data: buf }).promise;
              } catch {
                pdf = await pdfjsLib.getDocument({ url: String(bg) }).promise;
              }
              const page = await pdf.getPage(1);
              const scale = 2.25;
              currentScale = scale;
              const viewport = page.getViewport({ scale });
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d")!;
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              await page.render({ canvasContext: ctx, viewport }).promise;
              const dataURL = canvas.toDataURL("image/png");
              if (!mountedRef.current) return;
              setBgSrc(dataURL);
              setStageSize({ width: canvas.width, height: canvas.height });
              setRenderScale(scale);

              try {
                const view = (page as any).view as number[]; // CropBox
                const mediaBox = (page as any).mediaBox as number[]; // MediaBox (toàn bộ trang)

                const xMin = view[0] || 0;
                const yMax = view[3] || 0;

                // Tính toán lề so với MediaBox (thường backend dùng MediaBox làm gốc)
                const mHeight = mediaBox
                  ? Math.abs(mediaBox[3] - mediaBox[1])
                  : Math.abs(view[3] - view[1]);
                const mWidth = mediaBox
                  ? Math.abs(mediaBox[2] - mediaBox[0])
                  : Math.abs(view[2] - view[0]);

                const leftOffset = mediaBox ? xMin - mediaBox[0] : 0;
                const topOffset = mediaBox ? mediaBox[3] - yMax : 0;

                const ptsWidth = Math.abs((view?.[2] || 0) - xMin);
                const ptsHeight = Math.abs(yMax - (view?.[1] || 0));

                setPdfOrigin({ x: leftOffset, y: topOffset });
                setPageSize({
                  width: Math.round(ptsWidth),
                  height: Math.round(ptsHeight),
                });
              } catch (_) {
                setPageSize({
                  width: Math.round(canvas.width / scale),
                  height: Math.round(canvas.height / scale),
                });
              }
            } catch (err) {
              console.error("PDF load error", err);
            }
          } else {
            setBgSrc(String(bg));
            const pw = Number(initialPage?.pageWidth) || 800;
            const ph = Number(initialPage?.pageHeight) || 1130;
            setStageSize({ width: pw, height: ph });
            setPageSize({ width: pw, height: ph });
          }
        } else {
          const pw = Number(initialPage?.pageWidth) || 800;
          const ph = Number(initialPage?.pageHeight) || 1130;
          setStageSize({ width: pw, height: ph });
          setPageSize({ width: pw, height: ph });
        }

        let layoutArr: any[] = [];
        if (Array.isArray(initialPage?.layout)) {
          // Kiểm tra xem đây là mảng các phần tử (cũ) hay mảng các trang (mới)
          if (
            initialPage.layout.length > 0 &&
            Array.isArray(initialPage.layout[0]?.layout)
          ) {
            layoutArr = initialPage.layout[0].layout;
          } else {
            layoutArr = initialPage.layout;
          }
        }

        const ctx: Record<string, any> = (initialPage?.context || {}) as any;
        const next: EditorItem[] = layoutArr.map((it: any) => {
          const valueKey: string = String(it?.valueKey || "");
          const isBinding = /^\$\{.+\}$/.test(valueKey);

          const x = (Number(it?.x) || 0) * currentScale;
          const y = (Number(it?.y) || 0) * currentScale;

          if (it?.type === "qrcode" || String(it?.type) === "qrcode") {
            const id = uuidv4();
            const w =
              (Number(it?.w) || Number(it?.width) || 120) * currentScale;
            return {
              id,
              type: "qr",
              x,
              y,
              width: w,
              height: w,
              content: isBinding
                ? previewFromBinding(valueKey)
                : ctx && Object.prototype.hasOwnProperty.call(ctx, valueKey)
                ? String(ctx[valueKey] ?? "")
                : "HY9667MF5Q",
              bindingKey: isBinding ? valueKey : undefined,
              src: "",
            } as QRItem;
          }
          const id = uuidv4();
          return {
            id,
            type: "text",
            x,
            y,
            width: (Number(it?.w) || Number(it?.width) || 200) * currentScale,
            rotation: Number(it?.rotation) || 0,
            text: isBinding
              ? previewFromBinding(valueKey)
              : ctx && Object.prototype.hasOwnProperty.call(ctx, valueKey)
              ? String(ctx[valueKey] ?? "")
              : "",
            bindingKey: isBinding ? valueKey : undefined,
            fontSize: (Number(it?.fontSize) || 16) * currentScale,
            fill: String(it?.color || "#000000"),
            fontFamily: String(it?.fontFamily || "Arial"),
            align: String(it?.align || "left"),
            fontStyle: String(it?.fontStyle || "normal"),
            textDecoration: String(it?.textDecoration || ""),
          } as TextItem;
        });

        const hydrated = await Promise.all(
          next.map(async (el) => {
            if (el.type !== "qr") return el;
            const q = el as QRItem;
            const src = await QRCode.toDataURL(q.content || "HY9667MF5Q", {
              width: Math.max(20, Math.round(q.width)),
              margin: 1,
            });
            return { ...q, src } as QRItem;
          }),
        );

        if (mountedRef.current) {
          setElements(hydrated);
          if (typeof initialName === "string") setLayoutName(initialName);
          if (typeof initialDefault === "boolean") setIsDefault(initialDefault);
          setInitLoading(false);
        }
      } catch (e) {
        console.error("Init layout error", e);
        setInitLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPage, initialName, initialDefault]);

  const selectedItem = useMemo(
    () => elements.find((e) => e.id === selectedId) || null,
    [elements, selectedId],
  );

  useEffect(() => {
    if (!trRef.current || !layerRef.current) return;
    const layer = layerRef.current as any;
    const stage = (layer.getStage && layer.getStage()) || stageRef.current;
    if (!stage) return;
    if (selectedId) {
      const node = stage.findOne(`#node-${selectedId}`);
      if (node) {
        trRef.current.nodes([node]);
        layer.batchDraw();
      } else {
        trRef.current.nodes([]);
        layer.batchDraw();
      }
    } else {
      trRef.current.nodes([]);
      layer.batchDraw();
    }
  }, [selectedId, elements]);

  const handleStageMouseDown = (e: any) => {
    // click vào vùng trống để bỏ chọn
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
      return;
    }
    // nếu click background thì cũng bỏ chọn
    if (e.target && e.target.name && e.target.name() === "background") {
      setSelectedId(null);
    }
  };

  const renderPdfFirstPageToDataURL = async (file: File) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const scale = 2.25;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataURL = canvas.toDataURL("image/png");
    if (!mountedRef.current) return;
    setBgSrc(dataURL);
    setStageSize({ width: canvas.width, height: canvas.height });
    setRenderScale(scale);
    // Lưu kích thước trang gốc theo đơn vị PDF points (pt)
    try {
      // page.view: [xMin, yMin, xMax, yMax] đơn vị point (1pt = 1/72 inch)
      const view = (page as any).view as number[];
      const ptsWidth = Math.abs((view?.[2] || 0) - (view?.[0] || 0));
      const ptsHeight = Math.abs((view?.[3] || 0) - (view?.[1] || 0));
      const w = Math.round(ptsWidth);
      const h = Math.round(ptsHeight);
      setPageSize({
        width: w || Math.round(canvas.width / scale),
        height: h || Math.round(canvas.height / scale),
      });
    } catch (_) {
      setPageSize({
        width: Math.round(canvas.width / scale),
        height: Math.round(canvas.height / scale),
      });
    }
  };

  const handleBackgroundFile = async (file: File) => {
    try {
      const hide = message.loading("Đang upload background...", 0);
      try {
        const formData = new FormData();
        formData.append("files", file);
        const rs: any = await uploadFile(`/api/file/v2/upload-file`, formData);
        const url = rs?.created?.[0]?.url || rs?.data?.created?.[0]?.url;
        if (url) setBgUrl(url);
      } finally {
        hide();
      }
      await renderPdfFirstPageToDataURL(file);
    } catch (e) {
      message.error("Upload background thất bại");
    }
  };

  const addText = () => {
    const id = uuidv4();
    const baseX = 100;
    const baseY = 100;
    const margin = 10;
    const defaultWidth = 200;
    const safeWidth = Math.max(
      50,
      Math.min(defaultWidth, stageSize.width - margin * 2),
    );
    const safeX = Math.min(
      Math.max(baseX, 0),
      Math.max(stageSize.width - margin, 0),
    );
    const safeY = Math.min(
      Math.max(baseY, 0),
      Math.max(stageSize.height - margin, 0),
    );

    const item: TextItem = {
      id,
      type: "text",
      x: safeX,
      y: safeY,
      text: "Text mới",
      fontSize: 20,
      fill: "#000000",
      width: safeWidth,
      rotation: 0,
      fontFamily: "Arial",
      align: "left",
      fontStyle: "normal",
      textDecoration: "",
    };
    setElements((prev) => [...prev, item]);
    setSelectedId(id);
    ensureFontLoaded(item.fontFamily, item.fontSize).then(() => {
      if (mountedRef.current) layerRef.current?.batchDraw();
    });
  };

  const generateQrDataURL = async (content: string, size: number) => {
    return QRCode.toDataURL(content || "HY9667MF5Q", {
      width: size,
      margin: 1,
    });
  };

  /* eslint-disable no-template-curly-in-string */
  const SYSTEM_OPTIONS = [
    { label: "Mã đơn hàng", value: "${sys:ORDER_CODE}" },
    { label: "Mã vé (QR)", value: "${sys:TICKET_CODE}" },
    { label: "Tên người tham dự", value: "${sys:ATTENDEE_NAME}" },
  ];
  const EVENT_SPECIAL_OPTIONS = [
    { label: "Ngày bắt đầu sự kiện", value: "${event:START_DATE}" },
    { label: "Giờ bắt đầu sự kiện", value: "${event:START_TIME}" },
    { label: "Giờ vào cửa", value: "${event:GATE_OPEN_TIME}" },
    { label: "Tên sự kiện", value: "${event:NAME_EVENT}" },
    { label: "Địa điểm", value: "${event:STADIUM_NAME}" },
    { label: "Giá vé", value: "${event:PRICE}" },
  ];
  /* eslint-enable no-template-curly-in-string */

  const addQR = async () => {
    const id = uuidv4();
    const size = 150;
    const content = "HY9667MF5Q";
    const src = await generateQrDataURL(content, size);
    if (!mountedRef.current) return;
    const item: QRItem = {
      id,
      type: "qr",
      x: 100,
      y: 100,
      content,
      width: size,
      height: size,
      src,
    };
    setElements((prev) => [...prev, item]);
    setSelectedId(id);
  };

  const formatPrice = (p: any) => {
    if (p == null || p === "") return "";
    const n = Number(p);
    if (isNaN(n)) return String(p);
    return n.toLocaleString("vi-VN") + " VNĐ";
  };

  const previewFromBinding = (binding?: string): string => {
    if (!binding || typeof binding !== "string") return "";
    const decode = (s: string) => s.replace(/^\$\{/, "").replace(/\}$/, "");
    const key = decode(binding);
    const [scope, name] = key.split(":");
    if (scope === "sys") {
      switch (name) {
        case "ORDER_CODE":
          return "ORD-2025-0001";
        case "TICKET_CODE":
          return "HY9667MF5Q";
        case "ATTENDEE_NAME":
          return "Nguyen Van A";
        default:
          return "";
      }
    }
    if (scope === "event") {
      if (name === "START_DATE") {
        const v = eventValues?.startDateEvent;
        const d = v ? dayjs(v) : null;
        return d && d.isValid() ? d.format("DD.MM.YY") : "";
      }
      if (name === "START_TIME") {
        const v = eventValues?.startDateEvent;
        const d = v ? dayjs(v) : null;
        return d && d.isValid() ? d.format("HH:mm") : "";
      }
      if (name === "GATE_OPEN_TIME") {
        const v = eventValues?.checkInTime;
        const d = v ? dayjs(v) : null;
        return d && d.isValid() ? d.format("HH:mm") : "";
      }
      if (name === "NAME_EVENT") {
        return eventValues?.nameEvent || "";
      }
      if (name === "STADIUM_NAME") {
        return eventValues?.stadium?.name || "";
      }
      if (name === "PRICE") {
        return formatPrice(eventValues?.price);
      }
      const list = eventMeta || [];
      const item = list.find(
        (m) =>
          String(m?.name || "").toUpperCase() ===
          String(name || "").toUpperCase(),
      );
      return item?.value ?? "";
    }
    if (scope === "ticket") {
      const list = ticketMeta || [];
      const item = list.find(
        (m) =>
          String(m?.name || "").toUpperCase() ===
          String(name || "").toUpperCase(),
      );
      return item?.value ?? "";
    }
    return "";
  };

  const updateItem = (id: string, updater: (old: EditorItem) => EditorItem) => {
    setElements((prev) => prev.map((e) => (e.id === id ? updater(e) : e)));
  };

  const handleDragEnd = (id: string, e: any) => {
    const node = e.target;
    updateItem(
      id,
      (old) => ({ ...old, x: node.x(), y: node.y() } as EditorItem),
    );
  };

  const handleTransformEnd = (id: string, e: any) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const newW = Math.max(10, node.width() * scaleX);
    const newH = Math.max(10, node.height() * scaleY);
    node.scaleX(1);
    node.scaleY(1);

    updateItem(id, (old) => {
      if (old.type === "qr") {
        // QR là ảnh nên lưu width/height mới
        return { ...(old as QRItem), width: newW, height: newH };
      }
      // Text không scale bằng transformer, bỏ qua (fontSize chỉnh ở panel)
      return old;
    });
  };

  const exportJSON = () => {
    const mapBindingToValueKey = (binding?: string) => {
      if (!binding) return undefined;
      const s = String(binding).replace(/^\$\{/, "").replace(/\}$/, "");
      const [scope, nameRaw] = s.split(":");
      const name = String(nameRaw || "").toUpperCase();
      if (scope === "sys") {
        if (name === "ORDER_CODE") return "orderCode";
        if (name === "TICKET_CODE") return "ticketCode";
        if (name === "ATTENDEE_NAME") return "customerName";
        if (name === "TICKET_NAME") return "ticketName";
      }
      if (scope === "event") {
        if (name === "NAME_EVENT") return "eventName";
        if (name === "STADIUM_NAME") return "stadiumName";
        if (name === "PRICE") return "price";
        if (name === "START_DATE") return "eventStartDate";
        if (name === "START_TIME") return "eventStartTime";
        if (name === "GATE_OPEN_TIME") return "gateOpenTime";
        // Cho phép map meta event theo tên gốc
        return `eventMeta.${nameRaw}`;
      }
      if (scope === "ticket") {
        return `ticketMeta.${nameRaw}`;
      }
      return undefined;
    };

    let textAutoIdx = 0;
    let qrAutoIdx = 0;

    const context: any = {};
    const requiredKeys = new Set<string>();

    const toPt = (n: number) => Math.round((n / (renderScale || 1)) * 10) / 10;

    const layout = elements.map((el) => {
      if (el.type === "text") {
        const t = el as TextItem;
        const valueKey =
          mapBindingToValueKey(t.bindingKey) ||
          (textAutoIdx++ === 0 ? "text" : `text_${textAutoIdx}`);
        requiredKeys.add(valueKey);
        if (!t.bindingKey) {
          context[valueKey] = t.text || "";
        }
        return {
          type: "text",
          x: toPt(t.x),
          y: toPt(t.y),
          w: toPt(t.width),
          h: toPt(t.fontSize * 1.2),
          fontSize: Math.round(t.fontSize / (renderScale || 1)),
          align: t.align || "left",
          color: t.fill,
          fontFamily: t.fontFamily || "Arial",
          fontStyle: t.fontStyle || "normal",
          textDecoration: t.textDecoration || "",
          valueKey,
        } as any;
      }
      const q = el as QRItem;
      const valueKey =
        mapBindingToValueKey(q.bindingKey) ||
        (qrAutoIdx++ === 0 ? "code" : `qrcode_${qrAutoIdx}`);
      requiredKeys.add(valueKey);
      if (!q.bindingKey) {
        context[valueKey] = q.content || "";
      }
      return {
        type: "qrcode",
        x: toPt(q.x),
        y: toPt(q.y),
        w: toPt(q.width),
        h: toPt(q.height),
        valueKey,
      } as any;
    });

    // Chỉ bơm context cho các key thực sự xuất hiện trong layout
    const ensure = (key: string, value: any) => {
      if (requiredKeys.has(key) && context[key] === undefined) {
        context[key] = value;
      }
    };

    ensure("eventName", eventValues?.nameEvent || "");
    ensure("ticketName", "Ticket name");
    ensure("customerName", "Khách hàng");
    ensure("ticketCode", "HY9667MF5Q");
    ensure("orderCode", "ORD-2025-0001");
    ensure(
      "eventStartDate",
      eventValues?.startDateEvent
        ? dayjs(eventValues.startDateEvent).format("YYYY-MM-DD")
        : "",
    );
    ensure(
      "eventStartTime",
      eventValues?.startDateEvent
        ? dayjs(eventValues.startDateEvent).format("HH:mm")
        : "",
    );
    ensure(
      "gateOpenTime",
      eventValues?.checkInTime
        ? dayjs(eventValues.checkInTime).format("HH:mm")
        : "",
    );
    ensure("stadiumName", eventValues?.stadium?.name || "");
    ensure("price", eventValues?.price || "");

    // eventMeta.* và ticketMeta.* chỉ khi có trong requiredKeys
    Array.from(requiredKeys).forEach((k) => {
      if (k.startsWith("eventMeta.")) {
        const name = k.substring("eventMeta.".length);
        const item = (eventMeta || []).find(
          (m) => String(m?.name || "") === String(name),
        );
        ensure(k, item?.value ?? "");
      } else if (k.startsWith("ticketMeta.")) {
        const name = k.substring("ticketMeta.".length);
        const item = (ticketMeta || []).find(
          (m) => String(m?.name || "") === String(name),
        );
        ensure(k, item?.value ?? "");
      }
    });

    const payload = [
      {
        layout,
        background: bgUrl || bgSrc || "",
        context,
        pageWidth:
          pageSize && pageSize.width
            ? pageSize.width
            : Math.round(stageSize.width),
        pageHeight:
          pageSize && pageSize.height
            ? pageSize.height
            : Math.round(stageSize.height),
      },
    ];

    const text = JSON.stringify(payload, null, 2);
    setExportText(text);
    setExportVisible(true);
  };

  // Build layout + context for saving (keys exactly match valueKey used in layout)
  const buildSaveLayoutAndContext = () => {
    let textAutoIdx = 0;
    let qrAutoIdx = 0;
    const toPt = (n: number) => Math.round((n / (renderScale || 1)) * 10) / 10;

    const normalizeLayout = elements.map((el) => {
      if (el.type === "text") {
        const t = el as any;
        const valueKey = t.bindingKey
          ? String(t.bindingKey)
          : textAutoIdx++ === 0
          ? "text"
          : `text_${textAutoIdx}`;
        return {
          type: "text",
          x: toPt(t.x),
          y: toPt(t.y),
          w: toPt(t.width),
          h: toPt(t.fontSize * 1.2),
          fontSize: Math.round(t.fontSize / (renderScale || 1)),
          align: t.align || "left",
          color: t.fill,
          fontFamily: t.fontFamily || "Arial",
          fontStyle: t.fontStyle || "normal",
          textDecoration: t.textDecoration || "",
          valueKey,
        } as any;
      }
      const q = el as any;
      const valueKey = q.bindingKey
        ? String(q.bindingKey)
        : qrAutoIdx++ === 0
        ? "code"
        : `qrcode_${qrAutoIdx}`;
      return {
        type: "qrcode",
        x: toPt(q.x),
        y: toPt(q.y),
        w: toPt(q.width),
        h: toPt(q.height),
        valueKey,
      } as any;
    });

    const context: Record<string, any> = {};
    const decode = (s: string) => s.replace(/^\$\{/, "").replace(/\}$/, "");

    elements.forEach((el, idx) => {
      if (el.type === "text") {
        const t = el as TextItem;
        const key = String(normalizeLayout[idx]?.valueKey || "");
        if (t.bindingKey) {
          const [scope, name] = decode(String(t.bindingKey)).split(":");
          if (scope === "sys") {
            if (name === "ORDER_CODE") context[key] = "ORD-2025-0001";
            else if (name === "TICKET_CODE") context[key] = "HY9667MF5Q";
            else if (name === "ATTENDEE_NAME") context[key] = "Nguyen Van A";
            else context[key] = "";
          } else if (scope === "event") {
            if (name === "START_DATE") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              context[key] = d && d.isValid() ? d.format("DD.MM.YY") : "";
            } else if (name === "START_TIME") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              context[key] = d && d.isValid() ? d.format("HH:mm") : "";
            } else if (name === "GATE_OPEN_TIME") {
              const v = eventValues?.checkInTime;
              const d = v ? dayjs(v) : null;
              context[key] = d && d.isValid() ? d.format("HH:mm") : "";
            } else if (name === "NAME_EVENT") {
              context[key] = eventValues?.nameEvent || "";
            } else if (name === "STADIUM_NAME") {
              context[key] = eventValues?.stadium?.name || "";
            } else if (name === "PRICE") {
              context[key] = formatPrice(eventValues?.price);
            } else {
              const item = (eventMeta || []).find(
                (m) =>
                  String(m?.name || "").toUpperCase() ===
                  String(name || "").toUpperCase(),
              );
              context[key] = item?.value ?? "";
            }
          } else if (scope === "ticket") {
            const item = (ticketMeta || []).find(
              (m) =>
                String(m?.name || "").toUpperCase() ===
                String(name || "").toUpperCase(),
            );
            context[key] = item?.value ?? "";
          } else {
            context[key] = "";
          }
        } else {
          context[key] = t.text || "";
        }
      } else {
        const q = el as QRItem;
        const key = String(normalizeLayout[idx]?.valueKey || "");
        if (q.bindingKey) {
          const [scope, name] = decode(String(q.bindingKey)).split(":");
          if (scope === "sys") {
            if (name === "TICKET_CODE") context[key] = "HY9667MF5Q";
            else if (name === "ORDER_CODE") context[key] = "ORD-2025-0001";
            else if (name === "ATTENDEE_NAME") context[key] = "Nguyen Van A";
            else context[key] = "";
          } else if (scope === "event") {
            if (name === "START_DATE") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              context[key] = d && d.isValid() ? d.format("DD.MM.YY") : "";
            } else if (name === "START_TIME") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              context[key] = d && d.isValid() ? d.format("HH:mm") : "";
            } else if (name === "GATE_OPEN_TIME") {
              const v = eventValues?.checkInTime;
              const d = v ? dayjs(v) : null;
              context[key] = d && d.isValid() ? d.format("HH:mm") : "";
            } else if (name === "NAME_EVENT") {
              context[key] = eventValues?.nameEvent || "";
            } else if (name === "STADIUM_NAME") {
              context[key] = eventValues?.stadium?.name || "";
            } else if (name === "PRICE") {
              context[key] = formatPrice(eventValues?.price);
            } else {
              const item = (eventMeta || []).find(
                (m) =>
                  String(m?.name || "").toUpperCase() ===
                  String(name || "").toUpperCase(),
              );
              context[key] = item?.value ?? "";
            }
          } else if (scope === "ticket") {
            const item = (ticketMeta || []).find(
              (m) =>
                String(m?.name || "").toUpperCase() ===
                String(name || "").toUpperCase(),
            );
            context[key] = item?.value ?? "";
          } else {
            context[key] = "";
          }
        } else {
          context[key] = q.content || "";
        }
      }
    });

    return { normalizeLayout, context };
  };

  const createLayout = async () => {
    try {
      if (!layoutName.trim()) {
        message.warning("Nhập tên mẫu vé");
        return;
      }
      setSavingLayout(true);
      const { normalizeLayout, context } = buildSaveLayoutAndContext();
      const payload = [
        {
          layout: normalizeLayout,
          background: bgUrl || bgSrc || "",
          context,
          pageWidth: pageSize?.width || Math.round(stageSize.width),
          pageHeight: pageSize?.height || Math.round(stageSize.height),
        },
      ];
      const pageInfo = await helper.getPage(pageId || 120);
      const rs: any = await helper.callPageApi(
        pageInfo,
        "create_layout_ticket",
        {
          name: layoutName,
          default: Boolean(isDefault),
          background: bgUrl || bgSrc || "",
          layout: payload,
          eventId:
            (eventValues && (eventValues.id || eventValues.eventId)) ||
            undefined,
        },
      );
      message.success("Tạo layout thành công");
      onSaved && onSaved(rs?.data || rs);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
      message.error(e?.message || "Không thể tạo layout");
    } finally {
      setSavingLayout(false);
    }
  };

  const updateLayout = async () => {
    try {
      if (!layoutId) {
        message.warning("Không có layout cần cập nhật");
        return;
      }
      const nameToUse = layoutName.trim() || initialName || "";
      if (!nameToUse) {
        message.warning("Nhập tên mẫu vé");
        return;
      }
      setSavingLayout(true);
      const { normalizeLayout, context } = buildSaveLayoutAndContext();
      const payload = [
        {
          layout: normalizeLayout,
          background: bgUrl || bgSrc || initialPage?.background || "",
          context,
          pageWidth: pageSize?.width || Math.round(stageSize.width),
          pageHeight: pageSize?.height || Math.round(stageSize.height),
        },
      ];
      const pageInfo = await helper.getPage(pageId || 120);
      const rs: any = await helper.callPageApi(
        pageInfo,
        "update_layout_ticket",
        {
          id: layoutId,
          name: nameToUse,
          default: Boolean(isDefault),
          background: bgUrl || bgSrc || initialPage?.background || "",
          layout: payload,
          eventId:
            (eventValues && (eventValues.id || eventValues.eventId)) ||
            undefined,
        },
      );
      message.success("Cập nhật layout thành công");
      onSaved && onSaved(rs?.data || rs);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e);
      message.error(e?.message || "Không thể cập nhật layout");
    } finally {
      setSavingLayout(false);
    }
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setElements((prev) => prev.filter((e) => e.id !== selectedId));
    setSelectedId(null);
  };

  // Panel form handlers
  const [form] = Form.useForm();

  useEffect(() => {
    if (!selectedItem) {
      form.resetFields();
      return;
    }
    if (selectedItem.type === "text") {
      const t = selectedItem as TextItem;
      form.setFieldsValue({
        text: t.text,
        textVar: t.bindingKey || "__custom__",
        fontSize: t.fontSize,
        fill: t.fill,
        x_mm: pxToMm(t.x),
        y_mm: pxToMm(t.y),
        w_mm: pxToMm(t.width),
        rotation_deg: Math.round(t.rotation || 0),
        fontFamily: t.fontFamily,
        align: t.align,
        bold: t.fontStyle.includes("bold"),
        italic: t.fontStyle.includes("italic"),
        underline: t.textDecoration === "underline",
      });
    } else {
      const q = selectedItem as QRItem;
      form.setFieldsValue({
        qrContent: q.content,
        qrVar: q.bindingKey || "__custom__",
        qrWidth: Math.round(q.width),
      });
    }
  }, [selectedItem, form]);

  const onFormValuesChange = async (_: any, allValues: any) => {
    if (!selectedItem) return;
    if (selectedItem.type === "text") {
      const {
        text,
        textVar,
        fontSize,
        fill,
        x_mm,
        y_mm,
        w_mm,
        rotation_deg,
        fontFamily,
        align,
        bold,
        italic,
        underline,
      } = allValues;
      const resolvedText = (() => {
        if (
          textVar &&
          typeof textVar === "string" &&
          textVar !== "__custom__"
        ) {
          const decode = (s: string) =>
            s.replace(/^\$\{/, "").replace(/\}$/, "");
          const key = decode(textVar);
          const [scope, name] = key.split(":");
          if (scope === "sys") {
            // Hiển thị giá trị mẫu để preview
            switch (name) {
              case "ORDER_CODE":
                return "ORD-2025-0001";
              case "TICKET_CODE":
                return "HY9667MF5Q";
              case "ATTENDEE_NAME":
                return "Nguyen Van A";
              default:
                return "";
            }
          }
          if (scope === "event") {
            if (name === "START_DATE") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              return d && d.isValid() ? d.format("DD.MM.YY") : "";
            }
            if (name === "START_TIME") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              return d && d.isValid() ? d.format("HH:mm") : "";
            }
            if (name === "GATE_OPEN_TIME") {
              const v = eventValues?.checkInTime;
              const d = v ? dayjs(v) : null;
              return d && d.isValid() ? d.format("HH:mm") : "";
            }
            if (name === "NAME_EVENT") {
              return eventValues?.nameEvent || "";
            }
            if (name === "STADIUM_NAME") {
              return eventValues?.stadium?.name || "";
            }
            if (name === "PRICE") {
              return formatPrice(eventValues?.price);
            }
            const list = eventMeta || [];
            const item = list.find(
              (m) =>
                String(m?.name || "").toUpperCase() ===
                String(name || "").toUpperCase(),
            );
            return item?.value ?? "";
          }
          if (scope === "ticket") {
            const list = ticketMeta || [];
            const item = list.find(
              (m) =>
                String(m?.name || "").toUpperCase() ===
                String(name || "").toUpperCase(),
            );
            return item?.value ?? "";
          }
        }
        return text ?? "";
      })();
      const style =
        bold && italic
          ? "bold italic"
          : bold
          ? "bold"
          : italic
          ? "italic"
          : "normal";

      const targetFamily = fontFamily || (selectedItem as TextItem).fontFamily;
      const targetSize =
        Number(fontSize) || (selectedItem as TextItem).fontSize;

      // Cập nhật state ngay lập tức để tránh tranh chấp khi nhấn nút Lưu (Race Condition)
      updateItem(selectedItem.id, (old) => ({
        ...(old as TextItem),
        text: resolvedText,
        bindingKey:
          textVar && typeof textVar === "string" && textVar !== "__custom__"
            ? textVar
            : undefined,
        fontSize: Number(fontSize) || 12,
        fill: fill || "#000000",
        x: isNaN(Number(x_mm)) ? (old as TextItem).x : mmToPx(Number(x_mm)),
        y: isNaN(Number(y_mm)) ? (old as TextItem).y : mmToPx(Number(y_mm)),
        width: isNaN(Number(w_mm))
          ? (old as TextItem).width
          : mmToPx(Number(w_mm)),
        rotation: isNaN(Number(rotation_deg))
          ? (old as TextItem).rotation
          : Number(rotation_deg),
        fontFamily: targetFamily,
        align: (align as TextItem["align"]) || (old as TextItem).align,
        fontStyle: style as TextItem["fontStyle"],
        textDecoration: underline ? "underline" : "",
      }));

      // Tải font ngầm, không block state update
      ensureFontLoaded(targetFamily, targetSize).then(() => {
        if (mountedRef.current) {
          layerRef.current?.batchDraw();
        }
      });
    } else if (selectedItem.type === "qr") {
      const { qrContent, qrWidth, qrVar } = allValues;
      const size = Math.max(20, Number(qrWidth) || 100);
      const resolvedQR = (() => {
        if (qrVar && typeof qrVar === "string" && qrVar !== "__custom__") {
          const decode = (s: string) =>
            s.replace(/^\$\{/, "").replace(/\}$/, "");
          const key = decode(qrVar);
          const [scope, name] = key.split(":");
          if (scope === "sys") {
            if (name === "TICKET_CODE") return "HY9667MF5Q";
            if (name === "ORDER_CODE") return "ORD-2025-0001";
            if (name === "ATTENDEE_NAME") return "Nguyen Van A";
            return "";
          }
          if (scope === "event") {
            if (name === "START_DATE") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              return d && d.isValid() ? d.format("DD.MM.YY") : "";
            }
            if (name === "START_TIME") {
              const v = eventValues?.startDateEvent;
              const d = v ? dayjs(v) : null;
              return d && d.isValid() ? d.format("HH:mm") : "";
            }
            if (name === "GATE_OPEN_TIME") {
              const v = eventValues?.checkInTime;
              const d = v ? dayjs(v) : null;
              return d && d.isValid() ? d.format("HH:mm") : "";
            }
            if (name === "NAME_EVENT") {
              return eventValues?.nameEvent || "";
            }
            if (name === "STADIUM_NAME") {
              return eventValues?.stadium?.name || "";
            }
            if (name === "PRICE") {
              return formatPrice(eventValues?.price);
            }
            const list = eventMeta || [];
            const item = list.find(
              (m) =>
                String(m?.name || "").toUpperCase() ===
                String(name || "").toUpperCase(),
            );
            return item?.value ?? "";
          }
          if (scope === "ticket") {
            const list = ticketMeta || [];
            const item = list.find(
              (m) =>
                String(m?.name || "").toUpperCase() ===
                String(name || "").toUpperCase(),
            );
            return item?.value ?? "";
          }
        }
        return qrContent ?? "";
      })();
      const src = await generateQrDataURL(resolvedQR, size);
      if (!mountedRef.current) return;
      updateItem(selectedItem.id, (old) => ({
        ...(old as QRItem),
        content: resolvedQR,
        bindingKey:
          qrVar && typeof qrVar === "string" && qrVar !== "__custom__"
            ? qrVar
            : undefined,
        width: size,
        height: size,
        src,
      }));
    }
  };

  const applyFormPatch = (patch: any) => {
    form.setFieldsValue(patch);
    const all = form.getFieldsValue(true);
    onFormValuesChange({}, all);
  };
  const QRShape: React.FC<{ item: QRItem; isSelected: boolean }> = ({
    item,
    isSelected,
  }) => {
    const [img] = useImage(item.src);
    return (
      <KonvaImage
        id={`node-${item.id}`}
        image={img}
        x={item.x}
        y={item.y}
        width={item.width}
        height={item.height}
        draggable
        onClick={() => setSelectedId(item.id)}
        onTap={() => setSelectedId(item.id)}
        onDragEnd={(e) => handleDragEnd(item.id, e)}
        onTransformEnd={(e) => handleTransformEnd(item.id, e)}
      />
    );
  };

  const isBgLoading = bgSrc && bgStatus !== "loaded";

  return (
    <div style={{ height: "calc(80vh)", boxSizing: "border-box" }}>
      <Row style={{ height: "100%", margin: 0 }}>
        <Col xs={24} md={14} style={{ height: "100%", padding: 0 }}>
          <div
            ref={containerRef}
            style={{
              height: "100%",
              overflow: "auto",
              overflowX: "hidden",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              background: "#E8E8E8",
              padding: "40px 20px",
              boxSizing: "border-box",
            }}
          >
            {initLoading || containerWidth === 0 || isBgLoading ? (
              <div
                style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Skeleton active paragraph={{ rows: 8 }} />
              </div>
            ) : (
              <Stage
                ref={stageRef}
                width={stageSize.width * displayScale}
                height={stageSize.height * displayScale}
                scaleX={displayScale}
                scaleY={displayScale}
                style={{
                  background: "#fff",
                  boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                }}
                onMouseDown={handleStageMouseDown}
                onTouchStart={handleStageMouseDown}
              >
                <Layer ref={layerRef}>
                  {bgSrc && (
                    <KonvaImage
                      name="background"
                      image={bgImage}
                      width={stageSize.width}
                      height={stageSize.height}
                      listening={true}
                      onClick={() => setSelectedId(null)}
                    />
                  )}

                  {elements.map((el) => {
                    if (el.type === "text") {
                      const t = el as TextItem;
                      return (
                        <KonvaText
                          key={el.id}
                          id={`node-${el.id}`}
                          text={t.text}
                          x={t.x}
                          y={t.y}
                          width={t.width}
                          rotation={t.rotation || 0}
                          fontFamily={t.fontFamily}
                          align={t.align}
                          fontStyle={t.fontStyle}
                          textDecoration={t.textDecoration}
                          fontSize={t.fontSize}
                          fill={t.fill}
                          draggable
                          onClick={() => setSelectedId(el.id)}
                          onTap={() => setSelectedId(el.id)}
                          onDragEnd={(e) => handleDragEnd(el.id, e)}
                        />
                      );
                    }
                    if (el.type === "qr") {
                      return (
                        <QRShape
                          key={el.id}
                          item={el as QRItem}
                          isSelected={selectedId === el.id}
                        />
                      );
                    }
                    return null;
                  })}

                  <Transformer
                    ref={trRef}
                    rotateEnabled={false}
                    enabledAnchors={[
                      "top-left",
                      "top-right",
                      "bottom-left",
                      "bottom-right",
                    ]}
                    boundBoxFunc={(oldBox, newBox) => {
                      if (newBox.width < 20 || newBox.height < 20) {
                        return oldBox;
                      }
                      return newBox;
                    }}
                  />
                </Layer>
              </Stage>
            )}
          </div>
        </Col>

        <Col
          xs={24}
          md={10}
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderLeft: "1px solid #e8e8e8",
            background: "#fff",
            padding: 16,
          }}
        >
          <div
            style={{
              flex: 1,
              overflow: "auto",
            }}
          >
            <Divider orientation="left">Nền (PDF)</Divider>
            <input
              type="file"
              accept="application/pdf"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) await handleBackgroundFile(file);
              }}
            />

            <Divider orientation="left">Thêm đối tượng</Divider>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <Button onClick={addText}>Thêm Text</Button>
              <Button onClick={addQR}>Thêm QR</Button>
            </div>

            <Divider orientation="left">Lưu mẫu Layout</Divider>
            <Form layout="vertical">
              <Form.Item label="Tên mẫu vé">
                <Input
                  placeholder="Nhập tên mẫu"
                  value={layoutName}
                  onChange={(e) => setLayoutName(e.target.value)}
                />
              </Form.Item>
              <Form.Item label="Đặt làm mặc định" valuePropName="checked">
                <Switch checked={isDefault} onChange={setIsDefault} />
              </Form.Item>
              {layoutId ? (
                <Button
                  type="primary"
                  onClick={updateLayout}
                  loading={savingLayout}
                >
                  Cập nhật Layout
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={createLayout}
                  loading={savingLayout}
                >
                  Tạo Layout
                </Button>
              )}
            </Form>

            <Divider orientation="left">Thuộc tính item</Divider>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={onFormValuesChange}
            >
              {!selectedItem && (
                <div>Chọn một đối tượng trên canvas để chỉnh sửa</div>
              )}

              {selectedItem?.type === "text" && (
                <>
                  <Form.Item label="Nội dung">
                    <Row gutter={8}>
                      <Col span={12}>
                        <Form.Item
                          name="textVar"
                          noStyle
                          initialValue="__custom__"
                        >
                          <Select
                            options={[
                              { label: "Tự nhập", value: "__custom__" },
                              {
                                label: "— Hệ thống —",
                                value: "__sys__",
                                disabled: true,
                              },
                              ...SYSTEM_OPTIONS,
                              {
                                label: "— Sự kiện —",
                                value: "__event__",
                                disabled: true,
                              },
                              ...EVENT_SPECIAL_OPTIONS,
                              ...(eventMeta || []).map((m) => ({
                                label: `Event: ${m?.name}`,
                                value: "${event:" + String(m?.name || "") + "}",
                              })),
                              {
                                label: "— Vé —",
                                value: "__ticket__",
                                disabled: true,
                              },
                              ...(ticketMeta || []).map((m) => ({
                                label: `Ticket: ${m?.name}`,
                                value:
                                  "${ticket:" + String(m?.name || "") + "}",
                              })),
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="text" noStyle>
                          <Input
                            placeholder="Nhập nội dung"
                            disabled={
                              (form.getFieldValue("textVar") ||
                                "__custom__") !== "__custom__"
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form.Item>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item label="x (mm)" name="x_mm">
                        <InputNumber style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="y (mm)" name="y_mm">
                        <InputNumber style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item label="Chiều rộng (mm)" name="w_mm">
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Góc xoay (độ)" name="rotation_deg">
                        <InputNumber
                          min={-360}
                          max={360}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item label="Kích thước font" name="fontSize">
                        <InputNumber
                          min={6}
                          max={200}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Màu chữ"
                        name="fill"
                        initialValue="#000000"
                        valuePropName="value"
                      >
                        <input
                          type="color"
                          style={{
                            width: "100%",
                            height: 32,
                            padding: 0,
                            border: "none",
                            background: "transparent",
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        label="Phông chữ"
                        name="fontFamily"
                        initialValue="Open Sans"
                      >
                        <Select
                          options={[
                            {
                              value: "YTF VLeague Text",
                              label: "YTF VLeague Text",
                            },
                            { value: "Open Sans", label: "Open Sans" },
                            { value: "Roboto", label: "Roboto" },
                            { value: "Arial", label: "Arial" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Căn lề"
                        name="align"
                        initialValue="left"
                      >
                        <Button.Group style={{ width: "100%" }}>
                          <Button
                            type={
                              form.getFieldValue("align") === "left"
                                ? "primary"
                                : "default"
                            }
                            onClick={() => applyFormPatch({ align: "left" })}
                          >
                            <AlignLeftOutlined />
                          </Button>
                          <Button
                            type={
                              form.getFieldValue("align") === "center"
                                ? "primary"
                                : "default"
                            }
                            onClick={() => applyFormPatch({ align: "center" })}
                          >
                            <AlignCenterOutlined />
                          </Button>
                          <Button
                            type={
                              form.getFieldValue("align") === "right"
                                ? "primary"
                                : "default"
                            }
                            onClick={() => applyFormPatch({ align: "right" })}
                          >
                            <AlignRightOutlined />
                          </Button>
                        </Button.Group>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label="Kiểu chữ">
                    <Button.Group>
                      <Button
                        type={
                          form.getFieldValue("bold") ? "primary" : "default"
                        }
                        onClick={() =>
                          applyFormPatch({ bold: !form.getFieldValue("bold") })
                        }
                      >
                        B
                      </Button>
                      <Button
                        type={
                          form.getFieldValue("italic") ? "primary" : "default"
                        }
                        onClick={() =>
                          applyFormPatch({
                            italic: !form.getFieldValue("italic"),
                          })
                        }
                      >
                        I
                      </Button>
                      <Button
                        type={
                          form.getFieldValue("underline")
                            ? "primary"
                            : "default"
                        }
                        onClick={() =>
                          applyFormPatch({
                            underline: !form.getFieldValue("underline"),
                          })
                        }
                      >
                        U
                      </Button>
                    </Button.Group>
                  </Form.Item>

                  <Button danger onClick={deleteSelected}>
                    Xóa item
                  </Button>
                </>
              )}

              {selectedItem?.type === "qr" && (
                <>
                  <Form.Item label="Nội dung QR">
                    <Row gutter={8}>
                      <Col span={12}>
                        <Form.Item
                          name="qrVar"
                          noStyle
                          initialValue="__custom__"
                        >
                          <Select
                            options={[
                              { label: "Tự nhập", value: "__custom__" },
                              {
                                label: "— Hệ thống —",
                                value: "__sys__",
                                disabled: true,
                              },
                              ...SYSTEM_OPTIONS,
                              {
                                label: "— Sự kiện —",
                                value: "__event__",
                                disabled: true,
                              },
                              ...EVENT_SPECIAL_OPTIONS,
                              ...(eventMeta || []).map((m) => ({
                                label: `Event: ${m?.name}`,
                                value: "${event:" + String(m?.name || "") + "}",
                              })),
                              {
                                label: "— Vé —",
                                value: "__ticket__",
                                disabled: true,
                              },
                              ...(ticketMeta || []).map((m) => ({
                                label: `Ticket: ${m?.name}`,
                                value:
                                  "${ticket:" + String(m?.name || "") + "}",
                              })),
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="qrContent" noStyle>
                          <Input
                            placeholder="Nội dung để sinh QR"
                            disabled={
                              (form.getFieldValue("qrVar") || "__custom__") !==
                              "__custom__"
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form.Item>
                  <Form.Item label="Kích thước (px)" name="qrWidth">
                    <InputNumber min={20} max={800} style={{ width: "100%" }} />
                  </Form.Item>
                  <Button danger onClick={deleteSelected}>
                    Xóa item
                  </Button>
                </>
              )}
            </Form>

            <Divider />
            <div style={{ display: "flex", gap: 8 }}>
              <Button type="primary" onClick={exportJSON}>
                Export JSON
              </Button>
              {onClose && <Button onClick={onClose}>Đóng</Button>}
            </div>
            <Modal
              visible={exportVisible}
              onCancel={() => setExportVisible(false)}
              footer={null}
              width={720}
              title="Export JSON"
              destroyOnClose
            >
              <div style={{ marginBottom: 12 }}>
                <Button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(exportText || "");
                      message.success("Đã sao chép JSON vào clipboard");
                    } catch (_) {
                      message.error(
                        "Không thể sao chép, vui lòng copy thủ công",
                      );
                    }
                  }}
                >
                  Sao chép
                </Button>
              </div>
              <pre
                style={{
                  background: "#f6f8fa",
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 480,
                  overflow: "auto",
                }}
              >
                {exportText}
              </pre>
            </Modal>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default TicketLayoutEditor;
